// frontend/src/components/responsive-fixes/TableMobile.tsx
//
// Card-view fallback for tables that become unreadable strips below
// 768px (audit M3). Each row of the source table renders as a vertical
// glass-card with:
//   • primary field bold and large
//   • secondary fields as label-value pairs
//   • optional status as a colored pill
//   • optional actions (edit / delete) on the right
//
// CONSUMERS opt-in by replacing their <table> render with:
//
//   const r = useResponsiveLayout();
//   return (r.isPhone || r.isPhablet || r.isTablet)
//     ? <TableMobile columns={cols} rows={data} primaryKey="name" statusKey="status" statusTone={fn} />
//     : <ExistingDesktopTable />;
//
// This component renders nothing on desktop sizes by default; consumers
// gate it. Zero edits to existing dashboards required to ship this file.

import React from "react";
import { BRAND } from "../../branding";

export type StatusTone = "success" | "warning" | "danger" | "info" | "muted";

export interface TableMobileColumn<T = Record<string, unknown>> {
  /** Property name on the row object. */
  key: keyof T & string;
  /** Display label, e.g. "Owner" → label-value pair on the card. */
  label: string;
  /** Optional value formatter. Defaults to String(value). */
  format?: (value: unknown, row: T) => React.ReactNode;
  /** When true, render this field as the bold primary field at top. Only one column should set this. */
  primary?: boolean;
  /** When true, render this field as a status pill. Use with `statusTone`. */
  status?: boolean;
}

export interface TableMobileProps<T = Record<string, unknown>> {
  columns: TableMobileColumn<T>[];
  rows: T[];
  /** Optional fallback when the rows array is empty. */
  emptyText?: string;
  /** When provided and a column has `status: true`, returns a tone keyword for the pill color. */
  statusTone?: (row: T) => StatusTone;
  /** Optional click handler for an entire card (e.g. open detail page). */
  onRowClick?: (row: T) => void;
  /** Optional render-prop for trailing per-row actions (right side of the card). */
  renderActions?: (row: T) => React.ReactNode;
}

const TONE_COLORS: Record<StatusTone, { bg: string; fg: string; bd: string }> = {
  success: { bg: "rgba(207,237,162,0.10)", fg: "#cfeda2", bd: "rgba(207,237,162,0.32)" },
  warning: { bg: "rgba(255,214,165,0.10)", fg: "#ffd6a5", bd: "rgba(255,214,165,0.32)" },
  danger:  { bg: "rgba(255,107,107,0.10)", fg: "#ff6b6b", bd: "rgba(255,107,107,0.32)" },
  info:    { bg: "rgba(189,178,255,0.10)", fg: "#bdb2ff", bd: "rgba(189,178,255,0.32)" },
  muted:   { bg: "rgba(221,229,255,0.06)", fg: "rgba(221,229,255,0.55)", bd: "rgba(221,229,255,0.14)" },
};

function defaultFormat(v: unknown): React.ReactNode {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function TableMobile<T extends Record<string, unknown>>({
  columns,
  rows,
  emptyText = "No items.",
  statusTone,
  onRowClick,
  renderActions,
}: TableMobileProps<T>) {
  const primaryCol = columns.find((c) => c.primary) ?? columns[0];
  const statusCol = columns.find((c) => c.status);
  const secondaryCols = columns.filter(
    (c) => c !== primaryCol && c !== statusCol
  );

  if (!rows || rows.length === 0) {
    return (
      <div
        style={{
          padding: "24px 16px",
          textAlign: "center",
          color: "rgba(221,229,255,0.45)",
          fontFamily: BRAND.fontFamily,
          fontSize: 13,
          background: "rgba(15,25,47,0.4)",
          border: "0.5px dashed rgba(189,178,255,0.18)",
          borderRadius: 14,
        }}
      >
        {emptyText}
      </div>
    );
  }

  return (
    <div
      role="list"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        width: "100%",
      }}
    >
      {rows.map((row, idx) => {
        const tone: StatusTone = statusCol && statusTone ? statusTone(row) : "muted";
        const tc = TONE_COLORS[tone];
        const primaryValue = primaryCol
          ? (primaryCol.format ? primaryCol.format(row[primaryCol.key], row) : defaultFormat(row[primaryCol.key]))
          : null;
        const statusValue = statusCol
          ? (statusCol.format ? statusCol.format(row[statusCol.key], row) : defaultFormat(row[statusCol.key]))
          : null;

        return (
          <div
            key={idx}
            role="listitem"
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            style={{
              background: "rgba(15,25,47,0.55)",
              border: "0.5px solid rgba(189,178,255,0.14)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderRadius: 14,
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontFamily: BRAND.fontFamily,
              color: "#dde5ff",
              cursor: onRowClick ? "pointer" : "default",
              transition: "border-color 0.2s, background 0.2s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  flex: 1,
                  fontFamily: BRAND.fontFamily,
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#dde5ff",
                  letterSpacing: "-0.01em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {primaryValue}
              </div>
              {statusValue != null && (
                <span
                  style={{
                    background: tc.bg,
                    color: tc.fg,
                    border: `0.5px solid ${tc.bd}`,
                    borderRadius: 99,
                    padding: "3px 9px",
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {statusValue}
                </span>
              )}
              {renderActions && (
                <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", gap: 6 }}>
                  {renderActions(row)}
                </div>
              )}
            </div>

            {secondaryCols.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  gap: "6px 14px",
                  marginTop: 2,
                }}
              >
                {secondaryCols.map((c) => {
                  const value = c.format ? c.format(row[c.key], row) : defaultFormat(row[c.key]);
                  return (
                    <div key={c.key} style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "rgba(221,229,255,0.4)",
                          marginBottom: 2,
                        }}
                      >
                        {c.label}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "rgba(221,229,255,0.85)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {value}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default TableMobile;
